import { useState } from 'react';
import { Link } from 'react-router';
import type { ObjectMetadata, PossibleDuplicate } from '../../data/client';
import { useProposeMerge, useRecord } from '../../data/hooks';
import { Button, Icon, useToast } from '../../ui';
import { MOTION } from '../../ui/motion';
import { recordLabel } from '../pending/proposalDiff';

/**
 * ONE POSSIBLE DUPLICATE, AS ONE SENTENCE A COACH CAN ANSWER.
 *
 * WAVE 3 (R-B1, owner-39: "Copywriting wurde noch nicht vereinfach nehm ich an? ich checks
 * nicht"). What stood here was the mechanism: a capsule carrying a guard name out of
 * `identity-resolution.md` ("Too little in common"), a list of "Both hold the ..." lines, and
 * the server's own reasoning under an info glyph - four registers stacked on one row, none of
 * them the question the reader is actually being asked. It now reads as one sentence -
 * "They both hold the telegram chat id 123456 and nothing else." - with the button that
 * answers it, and the guard's own name and the engine's reasoning behind a disclosure for
 * whoever wants to know WHY the pair was held back.
 * hig: https://developer.apple.com/design/human-interface-guidelines/writing ("Choose simple,
 * plain language ... avoiding jargon", "Be clear. Choose words that are easily understood")
 *
 * THE GUARD IS STILL THE ROW'S MOST IMPORTANT FACT (identity-resolution.md 2.2) and it has
 * not moved out of the row - it moved into its second clause. A list of pairs with no reasons
 * is a list nobody trusts, and a list nobody trusts is worse than no list: the team learns to
 * click through it, which destroys the mechanism far more thoroughly than a missed duplicate
 * ever could. What the disclosure hides is the LONG FORM, not the reason.
 *
 * "Start a merge" creates a PROPOSAL, never a merge. There is no auto-merge at any
 * confidence (section 3), and a pair that a guard kept out of the queue is exactly the
 * pair a human has to look at side by side first.
 */
export interface DuplicateRowProps {
  readonly object: ObjectMetadata;
  readonly duplicate: PossibleDuplicate;
  /** Set on the record page: the row then names the OTHER record, not both. */
  readonly fromRecordId?: string | undefined;
}

/**
 * The guard, as the CLAUSE that finishes the evidence sentence, and as the NAME the
 * disclosure shows. Two forms of one fact, so the row can be read in one breath and audited
 * in two.
 *
 * `identity-resolution.md` 2.2 owns the guards; the names are its own and are kept verbatim
 * because they are what the duplicates page, the event log and this row have to agree on.
 */
const GUARD_LABEL: Readonly<Record<PossibleDuplicate['guard'], string>> = {
  none: 'Waiting for review',
  G1: 'Shared by too many records',
  G2: 'They contradict each other',
  G2b: 'Too little in common',
  G3: 'One of them is gone',
  G4: 'Already decided as different',
};

/** Reads on after the evidence, so the whole thing is one sentence. */
const GUARD_CLAUSE: Readonly<Record<PossibleDuplicate['guard'], string>> = {
  none: '. Nobody has looked at the pair yet.',
  G1: ', and so do so many other records that it says little about either of them.',
  G2: ', but they disagree about something else.',
  G2b: ' and nothing else.',
  G3: '. One of the two has been deleted.',
  G4: '. Somebody has already decided they are two different ones.',
};

export function DuplicateRow({ object, duplicate, fromRecordId }: DuplicateRowProps) {
  const toast = useToast();
  const propose = useProposeMerge();
  const [why, setWhy] = useState(false);
  const otherId =
    fromRecordId === duplicate.recordId ? duplicate.otherRecordId : duplicate.recordId;
  const left = useRecord(object.id, duplicate.recordId);
  const right = useRecord(object.id, duplicate.otherRecordId);

  const nameOf = (record: typeof left.data): string =>
    recordLabel(record ?? null, object.labelIdentifierFieldId) ?? 'Untitled';
  const leftName = nameOf(left.data);
  const rightName = nameOf(right.data);
  const otherName = otherId === duplicate.recordId ? leftName : rightName;

  /**
   * THE EVIDENCE, AS PROSE. One shared value is a clause; several are a list inside the same
   * sentence, because a pair that matches on two values is a stronger claim than one that
   * matches on one and the reader has to see both without counting rows.
   */
  const shared = duplicate.evidence.map(
    (entry) =>
      `${entry.isCurrent ? 'the' : 'the former'} ${entry.namespaceLabel.toLowerCase()} ${entry.valueNorm}`,
  );
  const evidence =
    shared.length === 0
      ? 'They match on an identifier neither of them holds anymore'
      : `They both hold ${listSentence(shared)}`;

  return (
    /**
     * A STACKED GROUP BOX ROW (R-B1). The row carries a name, a sentence, a disclosure and a
     * verb, which is more than a label-and-value line holds - `.record-row-stacked` is the
     * kit's own opt-out for exactly that, and it keeps the group box's hairline and inset so
     * the pair reads as one row of the same list the rest of the Overview is made of.
     */
    <li className="record-row record-row-stacked">
      {fromRecordId === undefined ? (
        <p className="text-sm text-ink">
          <RecordLink object={object} recordId={duplicate.recordId} name={leftName} />
          <span className="text-ink-muted"> and </span>
          <RecordLink object={object} recordId={duplicate.otherRecordId} name={rightName} />
        </p>
      ) : (
        <p className="text-sm text-ink">
          <RecordLink object={object} recordId={otherId} name={otherName} />
        </p>
      )}

      <p className="text-sm text-ink-muted">
        {evidence}
        {GUARD_CLAUSE[duplicate.guard]}
      </p>

      {/*
        THE LONG FORM, BEHIND A DISCLOSURE. The guard's own name and the engine's sentence are
        what somebody auditing the queue needs and what somebody clearing it does not; a
        disclosure is Apple's control for exactly that split, and the row's one-sentence
        version above is not a summary of the reason, it IS the reason in fewer words.
        hig: https://developer.apple.com/design/human-interface-guidelines/disclosure-controls
      */}
      <div>
        <button
          type="button"
          aria-expanded={why}
          onClick={() => setWhy((open) => !open)}
          className="pressable flex min-h-(--touch-target-min) items-center gap-1.5 text-2xs text-ink-muted"
        >
          <Icon
            name="chevron-right"
            size="0.8rem"
            className={`${MOTION.turn}${why ? ' rotate-90' : ''}`}
          />
          Why
        </button>
        <div hidden={!why} className="pb-1">
          <p className="text-2xs text-ink-muted">{GUARD_LABEL[duplicate.guard]}</p>
          <p className="text-2xs text-ink-subtle">
            {duplicate.reason.replace(/\bnamespace\b/gi, 'identifier type')}
          </p>
        </div>
      </div>

      <div>
        <Button
          variant="secondary"
          loading={propose.isPending}
          onClick={() =>
            propose.mutate(
              {
                objectId: object.id,
                recordA: duplicate.recordId,
                recordB: duplicate.otherRecordId,
              },
              {
                onSuccess: () =>
                  toast({
                    title: 'Merge proposed',
                    description:
                      'It is waiting under Pending changes, where an administrator sees both records side by side.',
                    tone: 'success',
                  }),
                onError: (error) =>
                  toast({
                    title: 'Unable to start the merge.',
                    description: error.message,
                    tone: 'danger',
                  }),
              },
            )
          }
        >
          Merge…
        </Button>
      </div>
    </li>
  );
}

/**
 * The record's name as the link, and NOTHING ELSE INSIDE IT. The accessible name of a link
 * is its whole text, so folding the sentence into it would announce a paragraph where a
 * screen reader user expects a destination - and it is what a test names the row by.
 *
 * NO PERMANENT UNDERLINE and no underline on hover: the wave-3 behaviour rule is that a row
 * never underlines under the pointer, and the accent role is what marks a link in Apple's own
 * text (HIG Color, "links").
 */
function RecordLink({
  object,
  recordId,
  name,
}: {
  readonly object: ObjectMetadata;
  readonly recordId: string;
  readonly name: string;
}) {
  return (
    <Link to={`/objects/${object.id}/records/${recordId}`} className="font-medium text-accent">
      {name}
    </Link>
  );
}

/** "a", "a and b", "a, b and c" - the shape English reads, not a comma-joined array. */
function listSentence(parts: readonly string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts.at(-1) ?? ''}`;
}
